import { useCallback, useState } from 'react'
import { AlertTriangle, CalendarClock, Info, Megaphone, Pin } from 'lucide-react'
import {
  useBannerAnnouncements,
  useDismissAnnouncement,
  useDismissPlatformAnnouncement,
  usePlatformAnnouncements,
} from '@/features/announcements/hooks/useAnnouncements'
import { useMaintenanceStore } from '@/stores/useMaintenanceStore'
import type { Announcement } from '@/types/announcement.types'
import { NoticeItem, type NoticeTone } from './NoticeItem'

const formatWhen = (value?: string): string | undefined =>
  value
    ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
        new Date(value),
      )
    : undefined

/**
 * The one strip of notices at the top of the app.
 *
 * Several sources used to produce separate strips stacked on top of each
 * other, which competed for the same attention and pushed the page down twice.
 * They share a shell and an ordering here, and nothing else: each source keeps
 * its own transport, its own polling and its own meaning of "dismissed",
 * because those genuinely differ.
 *
 * - **Scheduled maintenance** comes from the unauthenticated maintenance poll,
 *   through a store rather than a query. Dismissal is in memory and re-arms
 *   when a new window is set, so closing it hides a courtesy until reload
 *   rather than forever.
 * - **Platform announcements** come from the operator, authenticated, dismissed
 *   permanently per user in the database.
 * - **Tenant announcements** come from this organization's own administrators,
 *   same mechanism, different collection.
 *
 * Order is fixed rather than chronological: the platform going down outranks
 * anything the platform has to say, which outranks an internal notice. Billing
 * has its own strip above this one (BillingBanner). None of the authors can see the
 * others to judge that for themselves.
 *
 * Only the lockout screen has to reach a signed-out visitor, and it still does
 * — MaintenanceGate renders it above AuthInit. This strip is for people using
 * the app, so it lives inside the dashboard layout where the rest of the
 * chrome is.
 */
export function NoticeBanner(): React.ReactElement | null {
  const { state, message, startsAt, dismissedBanner, dismissBanner } = useMaintenanceStore()
  const { data: tenantFeed } = useBannerAnnouncements()
  const { data: platformFeed } = usePlatformAnnouncements()
  const dismissTenant = useDismissAnnouncement()
  const dismissPlatform = useDismissPlatformAnnouncement()

  // Hides a row the instant it is clicked, before the server round trip.
  const [justDismissed, setJustDismissed] = useState<Set<string>>(new Set())
  const hide = useCallback((id: string) => {
    setJustDismissed((previous) => new Set(previous).add(id))
  }, [])

  const maintenanceDue = state === 'scheduled' && !dismissedBanner
  const platform = (platformFeed ?? []).filter((item) => !justDismissed.has(item.id))
  const tenant = (tenantFeed?.data ?? []).filter((item) => !justDismissed.has(item._id))

  if (!maintenanceDue && platform.length === 0 && tenant.length === 0) return null

  const when = formatWhen(startsAt)

  return (
    <div className="space-y-2 px-6 pt-2">
      {maintenanceDue && (
        <NoticeItem
          tone="warning"
          icon={(className) => <CalendarClock className={className} aria-hidden />}
          title={when ? `Scheduled maintenance — starting ${when}` : 'Scheduled maintenance'}
          onDismiss={dismissBanner}
        >
          {message ?? 'The platform will be unavailable for a short period.'}
        </NoticeItem>
      )}

      {platform.map((item) => (
        <NoticeItem
          key={item.id}
          tone={item.severity as NoticeTone}
          icon={(className) =>
            item.severity === 'info' ? (
              <Info className={className} aria-hidden />
            ) : (
              <AlertTriangle className={className} aria-hidden />
            )
          }
          // Said plainly, because a message from the people who run the platform
          // carries different weight from one posted by a colleague.
          eyebrow="KAAMCO"
          title={item.title}
          onDismiss={item.dismissible ? () => {
            hide(item.id)
            dismissPlatform.mutate(item.id)
          } : undefined}
        >
          <Expandable text={item.body} />
        </NoticeItem>
      ))}

      {tenant.map((item) => (
        <NoticeItem
          key={item._id}
          tone="info"
          icon={(className) =>
            item.isPinned ? (
              <Pin className={className} aria-hidden />
            ) : (
              <Megaphone className={className} aria-hidden />
            )
          }
          eyebrow={item.isPinned ? 'Pinned' : undefined}
          title={item.title}
          footer={<TenantMeta announcement={item} />}
          onDismiss={() => {
            hide(item._id)
            dismissTenant.mutate(item._id)
          }}
        >
          <Expandable text={item.content} />
        </NoticeItem>
      ))}
    </div>
  )
}

/** Clamped to two lines until asked, so one long notice cannot own the page. */
function Expandable({ text }: { text: string }): React.ReactElement {
  const [expanded, setExpanded] = useState(false)
  return (
    <>
      <p className={expanded ? '' : 'line-clamp-2'}>{text}</p>
      {text.length > 150 && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="mt-1 text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        >
          {expanded ? 'Show Less' : 'Read More'}
        </button>
      )}
    </>
  )
}

function TenantMeta({ announcement }: { announcement: Announcement }): React.ReactElement | null {
  const author =
    typeof announcement.authorId === 'string'
      ? ''
      : `${announcement.authorId.firstName} ${announcement.authorId.lastName}`
  if (!author && !announcement.publishedAt) return null
  return (
    <>
      {author && (
        <span className="text-xs text-gray-500 dark:text-gray-500">— {author}</span>
      )}
      {announcement.publishedAt && (
        <span className="text-xs text-gray-400">
          {new Date(announcement.publishedAt).toLocaleDateString()}
        </span>
      )}
    </>
  )
}
